import { API_BASE_URL } from "@/config/api";
import { authenticatedApiClient, clearAccessToken, getAccessToken, setAccessToken } from "@/lib/authenticatedApiClient";
import { publicApiClient } from "@/lib/publicApiClient";
import { toRouteProviderToken } from "@/lib/providerIds";
import { normalizeEventTypeSummary, serializeCreateEventTypeRequest } from "@/domain/adapters/eventTypeAdapter";
import { type LoadingType } from "@/lib/networkActivity";
import type {
  AnswerInput,
  HostRescheduleContextResponse,
  ApiResponse,
  AuthResponse,
  AuthOnboardingResponse,
  LinkProviderResponse,
  AccountDeletionResponse,
  AvatarMetadata,
  HostBrandingDto,
  BillingOverviewDto,
  PurchasableBillingPlanDto,
  PublicAnnouncementDto,
  DiscountBreakdownDto,
  ReceiptDto,
  AvailabilityOverrideCreateRequest,
  AvailabilityOverrideResponse,
  BulkAvailabilityRulesUpsertRequest,
  CalendarStatusMap,
  BookingDetailResponse,
  CreateEventTypeRequest,
  UpdateEventTypeRequest,
  EventTypeSummaryResponse,
  EventAvailabilityScheduleRequest,
  EventAvailabilityScheduleResponse,
  MeetingSummaryResponse,
  OnboardingStateResponse,
  OnboardingUpdateRequest,
  HoldResponse,
  ProviderAwareStatusMap,
  PublicBookRequest,
  PublicConfirmResponse,
  PublicEventInfoResponse,
  PublicGroupSessionsResponse,
  PublicManageBookingResponse,
  PublicRescheduleRequest,
  RecurringWindowRequest,
  PinnedSessionResponse,
  RescheduleConflictResponse,
  SeriesOperationResponse,
  RecurringWindowResponse,
  RefreshRequest,
  SessionContextResponse,
  SessionRegistrationPageResponse,
  SessionSummaryResponse,
  SlotResponse,
  UserDto,
  BulkInviteMembersRequest,
  BulkInviteMembersResponse,
  TeamResponse,
  TeamDeletionImpactResponse,
  TeamMemberResponse,
  TeamInvitationResponse,
  CreateTeamRequest,
  InviteMemberRequest,
  ConferencingCoverageResponse,
  EventTypeParticipantResponse,
  AvailabilityRuleResponse,
  GroupReservationBlockerResponse,
  SetupStatusResponse,
  TeamReadinessSummaryResponse,
  RoundRobinStatsResponse,
  PublishReadinessResponse,
  CalendarEventsResponse,
  CalendarHolidaysResponse,
} from "./types";
import { ApiError } from "./types";

function toQuery(params?: Record<string, string | number | boolean | undefined>) {
  if (!params) return "";
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) search.set(key, String(value));
  });
  const q = search.toString();
  return q ? `?${q}` : "";
}

function unwrap<T>(body: ApiResponse<T> | T): T {
  if (typeof body === "object" && body !== null && "success" in body) {
    const wrapped = body as ApiResponse<T>;
    if (!wrapped.success) {
      throw new ApiError(wrapped.error?.code ?? "API_ERROR", wrapped.error?.message ?? "Request failed");
    }
    return wrapped.data as T;
  }
  return body as T;
}

export const api = {
  baseUrl: API_BASE_URL,

  getGoogleOAuthUrl() {
    return `${API_BASE_URL}/oauth2/authorization/google`;
  },

  getCalendarConnectUrl(params?: { source?: string; returnTo?: string; bookingSessionId?: string }) {
    return this.getIntegrationConnectUrl("calendar", "google", params);
  },

  async getCalendarConnectRedirectUrl(params?: { source?: string; returnTo?: string; bookingSessionId?: string }) {
    return this.getIntegrationConnectRedirectUrl("calendar", "google", params);
  },

  getIntegrationConnectUrl(
    kind: "calendar" | "conferencing",
    provider: string,
    params?: { source?: string; returnTo?: string; bookingSessionId?: string },
  ) {
    return `${API_BASE_URL}${this.integrationConnectPath(kind, provider, params)}`;
  },

  /** API-relative `/integrations/{kind}/{provider}/connect?…` path for the authenticated client. */
  integrationConnectPath(
    kind: "calendar" | "conferencing",
    provider: string,
    params?: { source?: string; returnTo?: string; bookingSessionId?: string },
  ) {
    const token = toRouteProviderToken(provider);
    const search = new URLSearchParams();
    if (params?.source) search.set("source", params.source);
    if (params?.returnTo) search.set("returnTo", params.returnTo);
    if (params?.bookingSessionId) search.set("bookingSessionId", params.bookingSessionId);
    const query = search.toString();
    return `/integrations/${kind}/${token}/connect${query ? `?${query}` : ""}`;
  },

  async getIntegrationConnectRedirectUrl(
    kind: "calendar" | "conferencing",
    provider: string,
    params?: { source?: string; returnTo?: string; bookingSessionId?: string },
  ) {
    // Route through the authenticated client so it carries the Bearer token AND gets the
    // built-in 401 -> silent-refresh -> retry. A raw fetch here (cookie-only) 401'd whenever the
    // access-token cookie had drifted from the still-valid localStorage token, and the calendar
    // OAuth could not even start.
    const body = await authenticatedApiClient<ApiResponse<{ redirectUrl: string }>>(
      this.integrationConnectPath(kind, provider, params),
      { method: "GET", loadingType: "user-blocking" },
    );
    if (!body.success || !body.data?.redirectUrl) {
      throw new ApiError("INTEGRATION_CONNECT_ERROR", `Failed to start ${provider} ${kind} connect.`);
    }
    return body.data.redirectUrl;
  },

  getEventInfo(username: string, slug: string) {
    return publicApiClient<ApiResponse<PublicEventInfoResponse>>(`/public/${username}/${slug}`).then(unwrap);
  },

  getActiveAnnouncements() {
    return publicApiClient<ApiResponse<PublicAnnouncementDto[]>>("/api/announcements/active").then(unwrap);
  },

  getAvailability(username: string, slug: string, date: string) {
    return publicApiClient<ApiResponse<SlotResponse>>(`/public/${username}/${slug}/availability${toQuery({ date })}`).then(unwrap);
  },

  getGroupSessions(username: string, slug: string, startDate?: string, days?: number) {
    return publicApiClient<ApiResponse<PublicGroupSessionsResponse>>(
      `/public/${username}/${slug}/group-sessions${toQuery({ startDate, days })}`,
    ).then(unwrap);
  },

  async holdSlot(username: string, slug: string, payload: PublicBookRequest, idempotencyKey?: string) {
    const raw = await publicApiClient<any>(`/public/${username}/${slug}/book`, {
      method: "POST",
      headers: {
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      body: JSON.stringify(payload),
    });
    return {
      bookingId: raw.bookingId,
      expiresAt: raw.expiresAt,
      status: raw.status,
      paymentRequired: raw.paymentRequired,
      paymentAmountMinor: raw.paymentAmountMinor,
      paymentCurrency: raw.paymentCurrency,
      paymentProvider: raw.paymentProvider,
    } as HoldResponse;
  },

  initializeBookingPayment(username: string, slug: string, bookingId: string, idempotencyKey: string) {
    return publicApiClient<ApiResponse<import("./types").PaymentInitializationResponse>>(
      `/public/${username}/${slug}/book/${bookingId}/payment`, {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
      },
    ).then(unwrap);
  },

  finalizeBookingPayment(username: string, slug: string, bookingId: string) {
    return publicApiClient<ApiResponse<import("./types").PublicPaymentStatusResponse>>(
      `/public/${username}/${slug}/book/${bookingId}/payment/finalize`, { method: "POST" },
    ).then(unwrap);
  },

  getBookingPaymentStatus(username: string, slug: string, bookingId: string) {
    return publicApiClient<ApiResponse<import("./types").PublicPaymentStatusResponse>>(
      `/public/${username}/${slug}/book/${bookingId}/payment`,
    ).then(unwrap);
  },

  // Attaches the guest to a slot that was already reserved. The public page holds the slot at
  // Continue, before it knows who is booking, so name and email arrive here instead of on the
  // hold. Returns the hold with a refreshed expiry — the server restarts the clock so a slow
  // form fill does not eat into the payment window.
  async updateGuestDetails(
    username: string,
    slug: string,
    bookingId: string,
    payload: { guestEmail: string; guestName: string; notes?: string; answers?: AnswerInput[]; embedToken?: string; guestEmails?: string[] },
  ) {
    const raw = await publicApiClient<ApiResponse<any>>(
      `/public/${username}/${slug}/book/${bookingId}/details`,
      { method: "PATCH", body: JSON.stringify(payload) },
    ).then(unwrap);
    return {
      bookingId: raw.bookingId,
      expiresAt: raw.expiresAt,
      paymentRequired: raw.paymentRequired,
      paymentAmountMinor: raw.paymentAmountMinor,
      paymentCurrency: raw.paymentCurrency,
      paymentProvider: raw.paymentProvider,
    } as HoldResponse;
  },

  // Gives up a hold the guest moved away from. Always safe to call: the server no-ops if the
  // booking has already been confirmed or expired.
  //
  // Deliberately kept out of the global loader. The guest has already navigated away from this
  // slot, so making them watch a full-screen spinner for a request whose outcome they will
  // never see is pure friction — and the expiry sweeper reclaims the slot even if it fails.
  releaseHold(username: string, slug: string, bookingId: string) {
    return publicApiClient(`/public/${username}/${slug}/book/${bookingId}/release`, {
      method: "POST",
      skipGlobalLoader: true,
    });
  },

  confirmBooking(username: string, slug: string, bookingId: string) {
    return publicApiClient<ApiResponse<PublicConfirmResponse>>(`/public/${username}/${slug}/book/${bookingId}/confirm`, {
      method: "POST",
    }).then(unwrap);
  },

  getPublicBooking(username: string, slug: string, bookingId: string, token: string) {
    return publicApiClient<ApiResponse<PublicManageBookingResponse>>(
      `/public/${username}/${slug}/book/${bookingId}${toQuery({ token })}`,
    ).then(unwrap);
  },

  cancelBooking(username: string, slug: string, bookingId: string, idempotencyKey?: string, token?: string) {
    return publicApiClient(`/public/${username}/${slug}/book/${bookingId}/cancel${toQuery({ token })}`, {
      method: "POST",
      headers: {
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
    });
  },

  rescheduleBooking(username: string, slug: string, bookingId: string, payload: PublicRescheduleRequest, idempotencyKey?: string, token?: string) {
    return publicApiClient(`/public/${username}/${slug}/book/${bookingId}/reschedule${toQuery({ token })}`, {
      method: "POST",
      headers: {
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      body: JSON.stringify(payload),
    });
  },

  getMe(options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {}) {
    return authenticatedApiClient<ApiResponse<UserDto>>("/api/me", options).then(unwrap);
  },

  getPaymentConnections() {
    return authenticatedApiClient<ApiResponse<import("./types").PaymentConnectionResponse[]>>(
      "/api/payment-connections",
    ).then(unwrap);
  },

  getAvailablePaymentProviders() {
    return authenticatedApiClient<ApiResponse<string[]>>(
      "/api/payment-connections/providers",
    ).then(unwrap);
  },

  startPaymentOnboarding(provider: string) {
    return authenticatedApiClient<ApiResponse<{ redirectUrl: string }>>(
      `/api/payment-connections/${provider.toLowerCase()}/onboarding`, { method: "POST" },
    ).then(unwrap);
  },

  refreshPaymentConnection(connectionId: string) {
    return authenticatedApiClient<ApiResponse<import("./types").PaymentConnectionResponse>>(
      `/api/payment-connections/${connectionId}/refresh`, { method: "POST" },
    ).then(unwrap);
  },

  disconnectPaymentConnection(connectionId: string) {
    return authenticatedApiClient<ApiResponse<void>>(
      `/api/payment-connections/${connectionId}`, { method: "DELETE" },
    ).then(unwrap);
  },

  getOnboardingState() {
    return authenticatedApiClient<ApiResponse<OnboardingStateResponse>>("/api/onboarding", {
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  updateOnboarding(payload: OnboardingUpdateRequest) {
    return authenticatedApiClient<ApiResponse<OnboardingStateResponse>>("/api/onboarding", {
      method: "PATCH",
      body: JSON.stringify(payload),
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  completeOnboarding() {
    return authenticatedApiClient<ApiResponse<OnboardingStateResponse>>("/api/onboarding/complete", {
      method: "POST",
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  autoConfigureOnboardingCalendar(connectionId: string) {
    return authenticatedApiClient<ApiResponse<OnboardingStateResponse>>(
      "/api/onboarding/calendar/auto-configure",
      {
        method: "POST",
        body: JSON.stringify({ connectionId }),
        skipGlobalLoader: true,
      },
    ).then(unwrap);
  },

  getAuthSession() {
    return authenticatedApiClient<ApiResponse<SessionContextResponse>>("/auth/session").then(unwrap);
  },

  getAuthProviders(options?: { loadingType?: LoadingType }) {
    return authenticatedApiClient<ApiResponse<AuthOnboardingResponse>>("/auth/providers", options).then(unwrap);
  },

  linkProvider(provider: string) {
    return authenticatedApiClient<ApiResponse<LinkProviderResponse>>(`/auth/link/${provider}`, {
      method: "POST",
    }).then(unwrap);
  },

  updateMyTimezone(timezone: string) {
    return authenticatedApiClient<ApiResponse<UserDto>>("/api/me/timezone", {
      method: "PUT",
      body: JSON.stringify({ timezone }),
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  getMyAvatarMetadata() {
    return authenticatedApiClient<ApiResponse<AvatarMetadata>>("/api/me/avatar", {
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  uploadMyAvatar(file: File) {
    const body = new FormData();
    body.append("file", file);
    return authenticatedApiClient<ApiResponse<UserDto>>("/api/me/avatar", {
      method: "PUT",
      body,
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  deleteMyAvatar() {
    return authenticatedApiClient<ApiResponse<UserDto>>("/api/me/avatar", {
      method: "DELETE",
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  // ── Host branding for public booking pages ──────────────────────────────────
  // Name, colour, logo and background need CUSTOM_BRANDING (Professional+), enforced server-side
  // on write. hideBunnycalBranding needs WHITE_LABEL (Team, or the Professional add-on) to set to
  // true -- but never to clear back to false, same "clearing is always allowed" rule the rest of
  // this endpoint follows. Both gates are re-checked server-side when a public page is read, so a
  // lapsed plan hides branding already saved without discarding it.

  getMyBranding() {
    return authenticatedApiClient<ApiResponse<HostBrandingDto>>("/api/me/branding", {
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  updateMyBranding(payload: {
    brandName: string | null;
    brandColor: string | null;
    backgroundType: "SOLID" | "LINEAR_GRADIENT" | null;
    backgroundColor: string | null;
    gradientStartColor: string | null;
    gradientEndColor: string | null;
    gradientAngle: number | null;
    textColor: string | null;
    /** Omit to leave the stored preference untouched. */
    hideBunnycalBranding?: boolean;
  }) {
    return authenticatedApiClient<ApiResponse<HostBrandingDto>>("/api/me/branding", {
      method: "PUT",
      body: JSON.stringify(payload),
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  uploadMyBrandLogo(file: File) {
    const body = new FormData();
    body.append("file", file);
    return authenticatedApiClient<ApiResponse<HostBrandingDto>>("/api/me/brand-logo", {
      method: "PUT",
      body,
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  deleteMyBrandLogo() {
    return authenticatedApiClient<ApiResponse<HostBrandingDto>>("/api/me/brand-logo", {
      method: "DELETE",
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  getBillingOverview() {
    return authenticatedApiClient<ApiResponse<BillingOverviewDto>>("/api/billing", {
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  listBillingPlans() {
    return authenticatedApiClient<ApiResponse<PurchasableBillingPlanDto[]>>("/api/billing/plans", {
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  startBillingCheckout(promoCode?: string, planId?: string) {
    return authenticatedApiClient<ApiResponse<{ redirectUrl: string; checkoutAttemptId: string }>>(
      "/api/billing/checkout",
      {
        method: "POST",
        body: JSON.stringify({ planId: planId ?? null, promoCode: promoCode ?? null }),
      },
    ).then(unwrap);
  },

  // What switching the current paid subscription to `targetPlanId` would cost right now. Drives
  // the change-plan confirmation, laid out so the user sees the proration in their favour:
  //   newPlanMinor (rest of the period) − creditMinor (unused current plan) = amountMinor due today
  // All amounts are BEFORE tax (added on the provider side); `totalWithTaxMinor` is the full
  // charge. All null when the provider couldn't quote one — the UI then proceeds without a figure.
  previewPlanChange(targetPlanId: string) {
    return authenticatedApiClient<
      ApiResponse<{
        amountMinor: number | null;
        newPlanMinor: number | null;
        creditMinor: number | null;
        taxMinor: number | null;
        totalWithTaxMinor: number | null;
        currency: string | null;
        effectiveAt: string | null;
      }>
    >("/api/billing/subscription/plan/preview", {
      method: "POST",
      body: JSON.stringify({ targetPlanId }),
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  // Moves the current paid subscription to a different paid plan in place — Dodo prorates and
  // charges the card on file; no redirect. The new plan lands once the provider confirms
  // (webhook), so callers should refetch the billing overview shortly after.
  changePlan(targetPlanId: string) {
    return authenticatedApiClient<ApiResponse<void>>("/api/billing/subscription/plan", {
      method: "POST",
      body: JSON.stringify({ targetPlanId }),
      // The dialog owns the loading UI ("Confirming…"); the full-screen global loader is wrong
      // here — it hides the dialog and then snaps back to it.
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  // After the provider redirect, the frontend asks the backend to verify the payment against the
  // provider (a real read) — this, not the redirect query params, is what confirms success. Safe
  // to poll; returns the current attempt status (CREATED/OPEN/PROCESSING/SUCCEEDED/FAILED/EXPIRED).
  // `currency` is the attempt's own stored currency, verified against the provider's session at
  // reconcile time (a mismatch fails the attempt) — safe to treat as authoritative once status is
  // SUCCEEDED. There is no amount/value field: the backend has none it can stand behind (see
  // CheckoutAttemptStatusDto's Javadoc for why).
  reconcileCheckoutAttempt(attemptId: string) {
    return authenticatedApiClient<ApiResponse<{ id: string; status: string; currency: string | null }>>(
      `/api/billing/checkout-attempts/${attemptId}/reconcile`,
      { method: "POST", skipGlobalLoader: true },
    ).then(unwrap);
  },

  getCheckoutAttempt(attemptId: string) {
    return authenticatedApiClient<ApiResponse<{ id: string; status: string; currency: string | null }>>(
      `/api/billing/checkout-attempts/${attemptId}`,
      { method: "GET", skipGlobalLoader: true },
    ).then(unwrap);
  },

  validatePromoCode(code: string, planId?: string) {
    return authenticatedApiClient<ApiResponse<DiscountBreakdownDto>>("/api/billing/promo/validate", {
      method: "POST",
      body: JSON.stringify({ planId: planId ?? null, code }),
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  openBillingPortal() {
    return authenticatedApiClient<ApiResponse<{ redirectUrl: string }>>("/api/billing/portal", {
      method: "POST",
    }).then(unwrap);
  },

  cancelSubscription(atPeriodEnd: boolean, reason?: string) {
    return authenticatedApiClient<ApiResponse<void>>("/api/billing/cancel", {
      method: "POST",
      body: JSON.stringify({ atPeriodEnd, reason: reason ?? null }),
    }).then(unwrap);
  },

  listReceipts() {
    return authenticatedApiClient<ApiResponse<ReceiptDto[]>>("/api/billing/receipts", {
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  // PDF is a binary download, so it bypasses the JSON-parsing client and fetches a blob.
  async downloadReceiptPdf(receiptId: string): Promise<Blob> {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/billing/receipts/${receiptId}/pdf`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to download receipt (${response.status})`);
    }
    return response.blob();
  },

  officialInvoice(receiptId: string) {
    return authenticatedApiClient<ApiResponse<{ downloadUrl: string; invoiceNumber?: string }>>(
      `/api/billing/receipts/${receiptId}/official-invoice`,
      { skipGlobalLoader: true },
    ).then(unwrap);
  },

  refresh(payload: RefreshRequest) {
    return authenticatedApiClient<ApiResponse<AuthResponse>>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify(payload),
    }).then((body) => {
      const data = unwrap(body);
      setAccessToken(data.accessToken);
      return data;
    });
  },

  logout() {
    return authenticatedApiClient("/auth/logout", {
      method: "POST",
    }).finally(() => {
      clearAccessToken();
    });
  },

  deleteAccount() {
    return authenticatedApiClient<ApiResponse<AccountDeletionResponse>>("/api/account", {
      method: "DELETE",
    }).then(unwrap);
  },

  getCalendarStatus() {
    return authenticatedApiClient<ApiResponse<CalendarStatusMap>>("/integrations/calendar/status", { skipGlobalLoader: true }).then(unwrap);
  },

  getCalendarProviderStatus() {
    return authenticatedApiClient<ApiResponse<ProviderAwareStatusMap>>("/integrations/calendar/status/providers").then(unwrap);
  },

  getConferencingStatus() {
    return authenticatedApiClient<ApiResponse<ProviderAwareStatusMap>>("/integrations/conferencing/status", { skipGlobalLoader: true }).then(unwrap);
  },

  disconnectCalendar(provider: string) {
    return authenticatedApiClient(`/integrations/calendar/${toRouteProviderToken(provider)}`, {
      method: "DELETE",
    });
  },

  /**
   * Disconnects one connected account. The provider-scoped route above removes every account for
   * that provider, which is the wrong thing once a user has connected more than one.
   */
  disconnectCalendarConnection(connectionId: string) {
    return authenticatedApiClient(`/integrations/calendar/connections/${connectionId}`, {
      method: "DELETE",
    });
  },

  /**
   * Re-points booking write-back at a specific connected account: every confirmed booking for this
   * user — their own event types and any team event they are on — is added to that account's
   * calendar. Applied optimistically in the UI, so it deliberately skips the global loader — the
   * selection has already moved and blocking the page behind it would just look like a stall.
   *
   * <p>If the previous native meeting-link provider is incompatible with the new calendar, the
   * server safely clears that default so the user can select the new calendar-backed provider next.
   */
  setDefaultWritebackConnection(connectionId: string) {
    return authenticatedApiClient(`/integrations/calendar/connections/${connectionId}/default-writeback`, {
      method: "POST",
      skipGlobalLoader: true,
    });
  },

  /**
   * Chooses which calendar *within* the write-back connection receives bookings. The connection is
   * picked by setDefaultWritebackConnection; this narrows it to one calendar inside that account.
   */
  setWritebackCalendar(connectionId: string, calendarId: string) {
    return authenticatedApiClient(
      `/integrations/calendar/connections/${connectionId}/calendars/${encodeURIComponent(calendarId)}/writeback`,
      { method: "PUT", skipGlobalLoader: true },
    );
  },

  /**
   * Turns conflict checking on or off for one calendar. This is a per-user setting with global
   * reach: the calendar is consulted (or not) for every booking the user can receive, including
   * team events they did not create.
   */
  setCalendarChecksAvailability(connectionId: string, calendarId: string, checksAvailability: boolean) {
    return authenticatedApiClient(
      `/integrations/calendar/connections/${connectionId}/calendars/${encodeURIComponent(calendarId)}/availability`,
      {
        method: "PUT",
        body: JSON.stringify({ checksAvailability }),
        skipGlobalLoader: true,
      },
    );
  },

  /**
   * Sets the user's default meeting link. Can reject with 400 when the current write-back calendar
   * cannot serve the requested provider — surface the returned message rather than a generic one.
   */
  setDefaultConferencingProvider(provider: "google_meet" | "microsoft_teams" | "zoom" | "none") {
    return authenticatedApiClient("/integrations/calendar/default-conferencing", {
      method: "PUT",
      body: JSON.stringify({ provider }),
      skipGlobalLoader: true,
    });
  },

  disconnectConferencing(provider: string) {
    return authenticatedApiClient(`/integrations/conferencing/${toRouteProviderToken(provider)}`, {
      method: "DELETE",
    });
  },

  listEventTypes() {
    return authenticatedApiClient<ApiResponse<EventTypeSummaryResponse[]>>("/api/event-types")
      .then(unwrap)
      .then((items) => items.map(normalizeEventTypeSummary));
  },

  getEventType(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<EventTypeSummaryResponse>>(`/api/event-types/${eventTypeId}`)
      .then(unwrap)
      .then(normalizeEventTypeSummary);
  },

  deleteEventType(
    eventTypeId: string,
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient(`/api/event-types/${eventTypeId}`, {
      method: "DELETE",
      ...options,
    });
  },

  publishEventType(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<PublishReadinessResponse>>(
      `/api/event-types/${eventTypeId}/publish`,
      { method: "PUT" },
    ).then(unwrap);
  },

  unpublishEventType(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<PublishReadinessResponse>>(
      `/api/event-types/${eventTypeId}/unpublish`,
      { method: "PUT" },
    ).then(unwrap);
  },

  listMyMeetings(params?: { upcomingOnly?: boolean; limit?: number; from?: string; to?: string; offset?: number }) {
    return authenticatedApiClient<ApiResponse<MeetingSummaryResponse[]>>(
      `/api/bookings/me/meetings${toQuery({
        upcomingOnly: params?.upcomingOnly,
        limit: params?.limit,
        from: params?.from,
        to: params?.to,
        offset: params?.offset,
      })}`,
      { skipGlobalLoader: true }
    ).then(unwrap);
  },

  getBookingDetail(bookingId: string) {
    return authenticatedApiClient<ApiResponse<BookingDetailResponse>>(
      `/api/bookings/${bookingId}`,
      { skipGlobalLoader: true }
    ).then(unwrap);
  },

  listCalendarEvents(params: { start: string; end: string }) {
    return authenticatedApiClient<ApiResponse<CalendarEventsResponse>>(
      `/api/calendar/events${toQuery({ start: params.start, end: params.end })}`,
      { skipGlobalLoader: true }
    ).then(unwrap);
  },

  listCalendarHolidays(params: { start: string; end: string }) {
    return authenticatedApiClient<ApiResponse<CalendarHolidaysResponse>>(
      `/api/calendar/holidays${toQuery({ start: params.start, end: params.end })}`,
      { skipGlobalLoader: true }
    ).then(unwrap);
  },

  listMySessions(params?: { status?: string; eventTypeId?: string; syncStatus?: string; from?: string; to?: string; cursor?: string; limit?: number }) {
    return authenticatedApiClient<ApiResponse<{ items: SessionSummaryResponse[]; nextCursor?: string | null; hasMore?: boolean }>>(
      `/api/sessions/me${toQuery({
        status: params?.status,
        eventTypeId: params?.eventTypeId,
        syncStatus: params?.syncStatus,
        from: params?.from,
        to: params?.to,
        cursor: params?.cursor,
        limit: params?.limit,
      })}`
    ).then(unwrap);
  },

  listSessionRegistrations(sessionId: string, params?: { status?: string; cursor?: string; limit?: number }) {
    return authenticatedApiClient<ApiResponse<SessionRegistrationPageResponse>>(
      `/api/sessions/${sessionId}/registrations${toQuery({
        status: params?.status,
        cursor: params?.cursor,
        limit: params?.limit,
      })}`
    ).then(unwrap);
  },

  cancelSession(sessionId: string, idempotencyKey?: string) {
    return authenticatedApiClient(`/api/sessions/${sessionId}/cancel`, {
      method: "POST",
      headers: {
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
    });
  },

  rescheduleSession(sessionId: string, startTime: string, acknowledgeExternalConflicts = false) {
    return authenticatedApiClient<ApiResponse<SessionSummaryResponse>>(`/api/sessions/${sessionId}/reschedule`, {
      method: "POST",
      body: JSON.stringify({ startTime, acknowledgeExternalConflicts }),
    }).then(unwrap);
  },

  previewRescheduleConflicts(sessionId: string, startTime: string) {
    return authenticatedApiClient<ApiResponse<RescheduleConflictResponse>>(
      `/api/sessions/${sessionId}/reschedule-conflicts${toQuery({ startTime })}`,
    ).then(unwrap);
  },

  listHostMeetings(hostId: string, params?: { upcomingOnly?: boolean; limit?: number }) {
    return authenticatedApiClient<ApiResponse<MeetingSummaryResponse[]>>(
      `/api/bookings/hosts/${hostId}/meetings${toQuery({
        upcomingOnly: params?.upcomingOnly,
        limit: params?.limit,
      })}`
    ).then(unwrap);
  },

  cancelHostBooking(bookingId: string, idempotencyKey?: string) {
    return authenticatedApiClient(`/api/bookings/${bookingId}/cancel`, {
      method: "POST",
      headers: {
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
    });
  },

  rescheduleHostBooking(bookingId: string, startTime: string, idempotencyKey?: string) {
    return authenticatedApiClient(`/api/bookings/${bookingId}/reschedule`, {
      method: "POST",
      headers: {
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      body: JSON.stringify({ startTime }),
    });
  },

  /**
   * Slots for the HOST reschedule picker. For a round-robin booking these are only the times the
   * acting host is personally free -- narrower than the team-wide union `getAvailability` returns.
   * A host reschedule keeps the host; moving to a teammate-only time is a hand-off instead.
   */
  getHostRescheduleAvailability(bookingId: string, date: string) {
    return authenticatedApiClient<ApiResponse<SlotResponse>>(
      `/api/bookings/${bookingId}/reschedule-availability${toQuery({ date })}`,
    ).then(unwrap);
  },

  /**
   * Event-type context for the host reschedule dialog when the acting host is assigned to the
   * booking but does NOT own the event type (a round-robin booking hosted by a participant, or
   * one that was handed off). The dashboard's own event-type list does not contain it.
   */
  getHostRescheduleContext(bookingId: string) {
    return authenticatedApiClient<ApiResponse<HostRescheduleContextResponse>>(
      `/api/bookings/${bookingId}/reschedule-context`,
    ).then(unwrap);
  },

  /**
   * Hand a round-robin booking to another available team member.
   *
   * Without `startTime`: keep the guest's time, find a teammate free then. Rejects with 409
   * (NO_ELIGIBLE_HOST) and leaves the booking untouched when nobody is — the caller then opens
   * the fallback picker with `getHandOffAvailability`.
   *
   * With `startTime`: move the meeting to that time and give it to whoever else covers it.
   * Rejects with 409 (SLOT_UNAVAILABLE) on a race, booking untouched.
   */
  handOffHostBooking(bookingId: string, startTime?: string, idempotencyKey?: string) {
    return authenticatedApiClient(`/api/bookings/${bookingId}/hand-off`, {
      method: "POST",
      headers: {
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      ...(startTime ? { body: JSON.stringify({ startTime }) } : {}),
    });
  },

  /**
   * Slots for the hand-off FALLBACK picker: the union of every OTHER team member's availability
   * (the acting host excluded). Used after a same-time hand-off finds nobody free.
   */
  getHandOffAvailability(bookingId: string, date: string) {
    return authenticatedApiClient<ApiResponse<SlotResponse>>(
      `/api/bookings/${bookingId}/hand-off-availability${toQuery({ date })}`,
    ).then(unwrap);
  },

  createEventType(payload: CreateEventTypeRequest) {
    const body = serializeCreateEventTypeRequest(payload);
    return authenticatedApiClient<ApiResponse<EventTypeSummaryResponse>>("/api/event-types", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then(unwrap)
      .then(normalizeEventTypeSummary);
  },

  /**
   * Partial update — omitted fields keep their stored value. This is the repair path for an event
   * type whose booking calendar is wrong or stale; before it existed, the only fix was deleting the
   * event type and rebuilding it.
   *
   * Bookings already made keep the calendar they were written to; only later ones follow the change.
   */
  updateEventType(eventTypeId: string, payload: UpdateEventTypeRequest) {
    return authenticatedApiClient<ApiResponse<EventTypeSummaryResponse>>(`/api/event-types/${eventTypeId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    })
      .then(unwrap)
      .then(normalizeEventTypeSummary);
  },

  getAvailabilityRules() {
    return authenticatedApiClient<ApiResponse<AvailabilityRuleResponse[]>>("/api/availability/rules").then(unwrap);
  },

  getReservationBlockers() {
    return authenticatedApiClient<ApiResponse<GroupReservationBlockerResponse[]>>(
      "/api/availability/reservation-blockers",
    ).then(unwrap);
  },

  // Host-global working hours. The ONLY writer of availability_rules. Must be
  // called exclusively from the dedicated Availability Settings experience -- never
  // from an event-type creation/edit flow.
  upsertAvailabilityRules(payload: BulkAvailabilityRulesUpsertRequest) {
    return authenticatedApiClient("/api/availability/rules/bulk", {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  // ── Event type participants (Phase 2) ──────────────────────────────────────
  listEventTypeParticipants(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<EventTypeParticipantResponse[]>>(
      `/api/event-types/${eventTypeId}/participants`,
    ).then(unwrap);
  },

  replaceEventTypeParticipants(eventTypeId: string, userIds: string[]) {
    return authenticatedApiClient<ApiResponse<EventTypeParticipantResponse[]>>(
      `/api/event-types/${eventTypeId}/participants`,
      {
        method: "PUT",
        body: JSON.stringify({ userIds }),
      },
    ).then(unwrap);
  },

  checkParticipantReadiness(userIds: string[]) {
    const params = userIds.map((id) => `userIds=${encodeURIComponent(id)}`).join("&");
    return authenticatedApiClient<ApiResponse<EventTypeParticipantResponse[]>>(
      `/api/event-types/participants/readiness?${params}`,
    ).then(unwrap);
  },

  /** Aggregate meeting-link coverage for a proposed roster. Counts only — never who lacks what. */
  checkConferencingCoverage(userIds: string[]) {
    const params = userIds.map((id) => `userIds=${encodeURIComponent(id)}`).join("&");
    return authenticatedApiClient<ApiResponse<ConferencingCoverageResponse>>(
      `/api/event-types/participants/conferencing-coverage?${params}`,
    ).then(unwrap);
  },

  // ── Teams (Phase 1: team foundation) ───────────────────────────────────────
  listTeams() {
    return authenticatedApiClient<ApiResponse<TeamResponse[]>>("/api/teams").then(unwrap);
  },

  createTeam(payload: CreateTeamRequest) {
    return authenticatedApiClient<ApiResponse<TeamResponse>>("/api/teams", {
      method: "POST",
      body: JSON.stringify(payload),
    }).then(unwrap);
  },

  getTeam(teamId: string) {
    return authenticatedApiClient<ApiResponse<TeamResponse>>(`/api/teams/${teamId}`).then(unwrap);
  },

  getTeamDeletionImpact(teamId: string) {
    return authenticatedApiClient<ApiResponse<TeamDeletionImpactResponse>>(
      `/api/teams/${teamId}/deletion-impact`,
    ).then(unwrap);
  },

  deleteTeam(
    teamId: string,
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient(`/api/teams/${teamId}`, {
      method: "DELETE",
      ...options,
    });
  },

  listTeamMembers(teamId: string) {
    return authenticatedApiClient<ApiResponse<TeamMemberResponse[]>>(
      `/api/teams/${teamId}/members`,
    ).then(unwrap);
  },

  removeTeamMember(
    teamId: string,
    memberUserId: string,
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient(`/api/teams/${teamId}/members/${memberUserId}`, {
      method: "DELETE",
      ...options,
    });
  },

  listTeamInvitations(teamId: string) {
    return authenticatedApiClient<ApiResponse<TeamInvitationResponse[]>>(
      `/api/teams/${teamId}/invitations`,
    ).then(unwrap);
  },

  inviteTeamMember(teamId: string, payload: InviteMemberRequest) {
    return authenticatedApiClient<ApiResponse<TeamInvitationResponse>>(
      `/api/teams/${teamId}/invitations`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ).then(unwrap);
  },

  /** One request for N invitees, so the blocking loader appears exactly once. */
  inviteTeamMembers(teamId: string, payload: BulkInviteMembersRequest) {
    return authenticatedApiClient<ApiResponse<BulkInviteMembersResponse>>(
      `/api/teams/${teamId}/invitations/bulk`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ).then(unwrap);
  },

  revokeTeamInvitation(
    teamId: string,
    invitationId: string,
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient(`/api/teams/${teamId}/invitations/${invitationId}`, {
      method: "DELETE",
      ...options,
    });
  },

  resendTeamInvitation(
    teamId: string,
    invitationId: string,
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient<ApiResponse<TeamInvitationResponse>>(
      `/api/teams/${teamId}/invitations/${invitationId}/resend`,
      { method: "POST", ...options },
    ).then(unwrap);
  },

  acceptTeamInvitation(
    token: string,
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient<ApiResponse<TeamMemberResponse>>(
      `/api/invitations/${token}/accept`,
      { method: "POST", ...options },
    ).then(unwrap);
  },

  // ── Team readiness & setup requests ──────────────────────────────────────

  getTeamReadinessSummary(teamId: string) {
    return authenticatedApiClient<ApiResponse<TeamReadinessSummaryResponse>>(
      `/api/teams/${teamId}/readiness-summary`,
    ).then(unwrap);
  },

  sendSetupRequest(teamMemberId: string) {
    return authenticatedApiClient<ApiResponse<SetupStatusResponse>>(
      `/api/teams/members/${teamMemberId}/setup-request`,
      { method: "POST" },
    ).then(unwrap);
  },

  getSetupStatus(teamMemberId: string) {
    return authenticatedApiClient<ApiResponse<SetupStatusResponse>>(
      `/api/teams/members/${teamMemberId}/setup-status`,
    ).then(unwrap);
  },

  // ── Round Robin stats ─────────────────────────────────────────────────────

  getRrStats(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<RoundRobinStatsResponse>>(
      `/api/event-types/${eventTypeId}/rr-stats`,
    ).then(unwrap);
  },

  getPublishReadiness(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<PublishReadinessResponse>>(
      `/api/event-types/${eventTypeId}/publish-readiness`,
    ).then(unwrap);
  },

  // GROUP event type reservation windows (ownership: blocks other event types).
  getReservationWindows(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<RecurringWindowResponse[]>>(
      `/api/event-types/${eventTypeId}/reservation-windows`,
    ).then(unwrap);
  },

  replaceReservationWindows(
    eventTypeId: string,
    windows: RecurringWindowRequest[],
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient<ApiResponse<RecurringWindowResponse[]>>(
      `/api/event-types/${eventTypeId}/reservation-windows`,
      {
        method: "PUT",
        body: JSON.stringify(windows),
        ...options,
      },
    ).then(unwrap);
  },

  /** Booked sessions left behind when the recurrence rule changed. */
  listPinnedSessions(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<PinnedSessionResponse[]>>(
      `/api/event-types/${eventTypeId}/sessions/pinned`,
    ).then(unwrap);
  },

  /**
   * Booked future sessions per window id. Powers the warning shown before an edit that
   * would pin sessions — windows with none are absent from the map.
   */
  countBookedSessionsByWindow(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<Record<string, number>>>(
      `/api/event-types/${eventTypeId}/sessions/booked-counts`,
    ).then(unwrap);
  },

  /** Queues pinned sessions onto the schedule the rule now describes. */
  movePinnedSessions(eventTypeId: string, sessionIds?: string[], startTime?: string) {
    return authenticatedApiClient<ApiResponse<SeriesOperationResponse>>(
      `/api/event-types/${eventTypeId}/sessions/move-pinned`,
      {
        method: "POST",
        body: JSON.stringify({ sessionIds: sessionIds ?? null, startTime: startTime ?? null }),
      },
    ).then(unwrap);
  },

  // Demand-driven event type availability FILTER windows (no ownership, no
  // reservation; only narrows this event's availability).
  getEventAvailabilityWindows(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<RecurringWindowResponse[]>>(
      `/api/event-types/${eventTypeId}/availability-windows`,
    ).then(unwrap);
  },

  replaceEventAvailabilityWindows(eventTypeId: string, windows: RecurringWindowRequest[]) {
    return authenticatedApiClient<ApiResponse<RecurringWindowResponse[]>>(
      `/api/event-types/${eventTypeId}/availability-windows`,
      {
        method: "PUT",
        body: JSON.stringify(windows),
      },
    ).then(unwrap);
  },

  getEventAvailabilitySchedule(eventTypeId: string) {
    return authenticatedApiClient<ApiResponse<EventAvailabilityScheduleResponse>>(
      `/api/event-types/${eventTypeId}/availability-schedule`,
    ).then(unwrap);
  },

  replaceEventAvailabilitySchedule(
    eventTypeId: string,
    schedule: EventAvailabilityScheduleRequest,
    options: { skipGlobalLoader?: boolean; loadingType?: LoadingType } = {},
  ) {
    return authenticatedApiClient<ApiResponse<EventAvailabilityScheduleResponse>>(
      `/api/event-types/${eventTypeId}/availability-schedule`,
      {
        method: "PUT",
        body: JSON.stringify(schedule),
        ...options,
      },
    ).then(unwrap);
  },

  getAvailabilityOverrides(from?: string, to?: string) {
    return authenticatedApiClient<ApiResponse<AvailabilityOverrideResponse[]>>(`/api/availability/overrides${toQuery({ from, to })}`, {
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  createAvailabilityOverride(payload: AvailabilityOverrideCreateRequest) {
    return authenticatedApiClient<ApiResponse<AvailabilityOverrideResponse>>("/api/availability/overrides", {
      method: "POST",
      body: JSON.stringify(payload),
      skipGlobalLoader: true,
    }).then(unwrap);
  },

  deleteAvailabilityOverride(id: string) {
    return authenticatedApiClient(`/api/availability/overrides/${id}`, {
      method: "DELETE",
      skipGlobalLoader: true,
    });
  },
};
